import React, { useState, useCallback, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { useTranslation } from 'react-i18next';
import pRetry from 'p-retry';
import { Message as Toast } from '@kargotech/tms-ui/components';
import VALIDATE_LOGIN_STATUS from './mutations/validateLoginStatus';
import translations from './translations';

import Auth from './Auth';

import AuthContext from './AuthContext';
import {
  AUTH_STATUS,
  DEFAULT_MAX_LOGIN_RETRIES,
  DEFAULT_MAX_VALIDATE_LOGIN_RETRIES,
  DEFAULT_RETRY_ATTEMPT_MINIMUM_TIMEOUT,
  LOGIN_STATUS_MAP,
  TRANSLATIONS_NAMESPACE,
} from './constants';
import {
  clearStorageItems,
  formatSignUpAuthData,
} from './utils';
import useCompanyAuth from './hooks/useCompanyAuth';

const AVAILABLE_TRANSLATION_LANGS = Object.keys(translations);

/**
 * A wrapper for auth module that uses a react context to hold several values.
 * This provider gives useful interface for consumer component to interact
 * with sessions. This provider only responsible for Authentication purposes.
 *
 * @param {object} prop - AuthProvider props
 * @param {object} prop.apiClient - client for apollo graphql hooks
 * @param {ReactElement} prop.children - children/consumer
 * @param {number} [prop.maxLoginPostRegisterRetries=5] - total retries attempt allowed to
 *   do login after successfully register an account (auto-login)
 * @param {number} [prop.maxValidateLoginRetries=5] - total retries attempt allowed to
 *   validate login status after successfully login to aws cognito
 * @param {Function} prop.onLoginSuccess - callback after login process succeed
 * @param {Function} prop.onLogoutSuccess - callback after logout process succeed
 * @param {Function} prop.onSelectCompanySuccess - callback after select company process succeed
 * @param {localStorage} [prop.persistentDataStorage=localStorage] - where to store selected company data
 * @param {string} prop.platformAccess - kind of platform the client is hitting
 * @param {Array} prop.preservedLocalStorageKeys - local storage item keys to be preserved from clearing
 *  after logout
 * @param {number} prop.retryAttemptMinimumTimeout - minimum timeout for next promise
 *  (login and validate login status) retry calls.
 * @returns {ReactElement} - AuthProvider
 */
function AuthProvider({
  apiClient: client, // Profile Service API Client
  children,
  maxLoginPostRegisterRetries = DEFAULT_MAX_LOGIN_RETRIES,
  maxValidateLoginRetries = DEFAULT_MAX_VALIDATE_LOGIN_RETRIES,
  onLoginSuccess = e => e,
  onLogoutSuccess = e => e,
  onSelectCompanySuccess = e => e,
  persistentDataStorage = localStorage,
  platformAccess,
  preservedLocalStorageKeys = [],
  retryAttemptMinimumTimeout = DEFAULT_RETRY_ATTEMPT_MINIMUM_TIMEOUT,
} = {}) {
  const [authStatus, setAuthStatus] = useState(AUTH_STATUS.LOADING);
  const [isFirstTimeLogin, setIsFirstTimeLogin] = useState(false);
  const {
    enrolledCompanies,
    fetchAndSaveEnrolledCompanies,
    resetEnrolledCompanies,
    resetSelectedCompany,
    selectedCompany,
    selectedCompanyKsuid,
    setSelectedCompany,
  } = useCompanyAuth({
    apiClient: client,
    onSelectCompanySuccess,
    persistentDataStorage,
    platformAccess,
  });

  const { t, i18n } = useTranslation([TRANSLATIONS_NAMESPACE, 'message']);

  useEffect(() => {
    AVAILABLE_TRANSLATION_LANGS.forEach(lang => {
      i18n.addResources(lang, TRANSLATIONS_NAMESPACE, translations[lang]);
    });
  }, [i18n, t]);

  const [validateLoginStatus] = useMutation(VALIDATE_LOGIN_STATUS, {
    ...(client ? { client } : {}),
  });

  const updateSessionStatus = useCallback(async (shouldFetchEnrolledCompanies = false) => {
    let isAuthenticated = false;

    try {
      await Auth.currentAuthenticatedUser();
      isAuthenticated = true;
    } catch {
      resetSelectedCompany();
      resetEnrolledCompanies();
    }
    setAuthStatus(isAuthenticated ? AUTH_STATUS.AUTHENTICATED : AUTH_STATUS.NOT_AUTHENTICATED);

    if (isAuthenticated && shouldFetchEnrolledCompanies) {
      await fetchAndSaveEnrolledCompanies();
    }

    return isAuthenticated;
  }, [fetchAndSaveEnrolledCompanies, resetEnrolledCompanies, resetSelectedCompany]);

  const logout = useCallback(async ({
    unexpectedLogout = false
  } = {}) => {
    if (!unexpectedLogout) {
      Toast.loading(t('logging_out'));
    }
    setIsFirstTimeLogin(false);
    try {
      await Auth.signOut();
      await updateSessionStatus();
      clearStorageItems(persistentDataStorage, preservedLocalStorageKeys);

      if (!unexpectedLogout) {
        Toast.destroy();
        Toast.success(t('logging_out_success'));
      }

      onLogoutSuccess();
    } catch {
      Toast.error(t('logging_out_failed'));
    }
  }, [onLogoutSuccess, persistentDataStorage, preservedLocalStorageKeys, t, updateSessionStatus]);

  // Wrapper function to handle post-API-call behavior
  // intended to be wrapped by pRetry
  const doValidateLoginStatus = useCallback(async () => {
    const validateLoginResponse = await validateLoginStatus();
    const loginStatus = validateLoginResponse?.data?.loginTms;

    // Throw error to trigger retries if login status equal 3
    if (loginStatus === LOGIN_STATUS_MAP.FAILED_TIMEOUT) {
      throw new Error(t('message:errors.unexpected'));
    }

    // Reject promise immediately if login status equal 2
    if (loginStatus === LOGIN_STATUS_MAP.FAILED_DEACTIVATED_FROM_COMPANIES) {
      throw new pRetry.AbortError(t('user_has_been_deactivated'));
    }

    return loginStatus;
  }, [t, validateLoginStatus]);

  const login = useCallback(async (username, password, {
    skipUnexpectedErrorToast = false
  } = {}) => {
    try {
      await Auth.signIn({
        username,
        password
      });

      try {
        const loginStatus = await pRetry(doValidateLoginStatus, {
          retries: maxValidateLoginRetries,
          minTimeout: retryAttemptMinimumTimeout,
        });

        setIsFirstTimeLogin(loginStatus === LOGIN_STATUS_MAP.SUCCESS_AS_FIRST_TIME_LOGIN);
        await updateSessionStatus(true);
        Toast.success(t('logging_in_success'));
        onLoginSuccess();
      } catch (error) {
        await logout({ unexpectedLogout: true });
        throw error;
      }
    } catch (error) {
      if (!skipUnexpectedErrorToast) {
        Toast.error(error.message);
      }
      throw error;
    }
  }, [
    doValidateLoginStatus,
    logout,
    maxValidateLoginRetries,
    onLoginSuccess,
    retryAttemptMinimumTimeout,
    t,
    updateSessionStatus,
  ]);

  const postRegisterLogin = useCallback(async (phoneNumber, password) => {
    try {
      await login(phoneNumber, password, {
        skipUnexpectedErrorToast: true
      });
    } catch (error) {
      // Retry login if user is still on sync from backend to aws cognito
      if (error.code === 'UserNotConfirmedException') {
        throw new Error(error);
      }

      throw new pRetry.AbortError(error);
    }
  }, [login]);

  const register = useCallback(async ({
    companyName,
    email,
    name,
    password,
    phoneNumber,
  }) => {
    // Sign up to AWS Cognito
    await Auth.signUp(formatSignUpAuthData({
      companyName,
      email,
      name,
      password,
      phoneNumber,
    }));

    // Do login and keep retrying if registered account
    // in cognito is marked as NotConfirmed
    await pRetry(() => postRegisterLogin(phoneNumber, password), {
      retries: maxLoginPostRegisterRetries,
      minTimeout: retryAttemptMinimumTimeout,
    });
  }, [maxLoginPostRegisterRetries, postRegisterLogin, retryAttemptMinimumTimeout]);

  return (
    <AuthContext.Provider
      value={{
        authStatus,
        enrolledCompanies,
        fetchAndSaveEnrolledCompanies,
        isFirstTimeLogin,
        login,
        logout,
        register,
        resetSelectedCompany,
        setIsFirstTimeLogin,
        selectedCompany,
        selectedCompanyKsuid,
        setSelectedCompany,
        updateSessionStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
